import { QueryClient, keepPreviousData } from "@tanstack/react-query";

/**
 * Singleton React Query client. Lives outside App.tsx so non-component code
 * (auth hooks, edge function callers) can clear or invalidate queries
 * without having to import App or pass the client through props.
 *
 * Defaults are tuned for "instant" dash transitions:
 *   - keepPreviousData: when a query refetches with new params (filters,
 *     pagination, route changes that share a key prefix), the previous
 *     result stays on screen until the new data arrives, so users don't
 *     see a skeleton flash mid-interaction.
 *   - staleTime / gcTime: hot data stays usable for 30s and lingers in
 *     cache for 5 min, so back-nav and tab switches restore instantly.
 *   - First-load skeletons are rendered by each page (or the route-level
 *     <Suspense> fallback in DashV2Layout) using `isPending`/`isLoading`,
 *     which keepPreviousData does NOT suppress on the very first fetch.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: "always",
      placeholderData: keepPreviousData,
    },
    mutations: { retry: 1 },
  },
});

